import React, { useRef, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    List, ListItem, ListItemText, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { API } from '../../api';
import { resultsTokens as t } from './tokens';

// Mismo límite de negocio que el backend (ver BULK_PERFORMANCE_MAX_FILE_SIZE
// en talentapp/api/reply.py). Es sólo una validación rápida en el cliente: la
// autoridad real es el backend.
const MAX_FILE_SIZE = 2 * 1024 * 1024;

// El backend devuelve errores como código + parámetros (ver BulkPerformanceError
// en talentapp/api/reply.py) para poder traducirlos a los tres idiomas acá,
// en vez de mostrar el texto ya armado en un solo idioma.
function formatGeneralError(code, params, tr) {
    switch (code) {
        case 'file_required': return tr('bulk_err_file_required');
        case 'invalid_extension': return tr('bulk_performance_invalid_extension');
        case 'file_too_large': return tr('bulk_performance_file_too_large');
        case 'invalid_xlsx': return tr('bulk_err_invalid_xlsx');
        case 'no_header_row': return tr('bulk_err_no_header_row');
        case 'missing_columns': {
            const fields = (params?.fields || []).map((f) => tr(`bulk_col_${f}`)).join(', ');
            return tr('bulk_err_missing_columns').replace('%1', fields);
        }
        case 'missing_identifier_column': return tr('bulk_err_missing_identifier_column');
        case 'too_many_rows': return tr('bulk_err_too_many_rows').replace('%1', params?.max_rows);
        default: return tr('bulk_performance_generic_error');
    }
}

function formatRowError({ row, code, params }, tr) {
    const prefix = tr('bulk_err_row').replace('%1', row);
    let detail;
    switch (code) {
        case 'missing_legajo_and_name': detail = tr('bulk_err_missing_legajo_and_name'); break;
        case 'invalid_performance': detail = tr('bulk_err_invalid_performance'); break;
        case 'duplicate_legajo':
            detail = tr('bulk_err_duplicate_legajo').replace('%1', params?.previous_row);
            break;
        case 'duplicate_person':
            detail = tr('bulk_err_duplicate_person').replace('%1', params?.previous_row);
            break;
        case 'name_ambiguous':
            detail = tr('bulk_err_name_ambiguous').replace('%1', params?.name);
            break;
        case 'not_found': detail = tr('bulk_err_not_found'); break;
        default: detail = tr('bulk_performance_generic_error');
    }
    return `${prefix}: ${detail}`;
}

export default function PerformanceImportDialog({ open, onClose, onImported, tr }) {
    const inputRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const [rowErrors, setRowErrors] = useState([]);
    const [successCount, setSuccessCount] = useState(null);
    const [skippedCount, setSkippedCount] = useState(0);

    const hasResult = successCount !== null || Boolean(generalError) || rowErrors.length > 0;

    const resetState = () => {
        setFileName('');
        setUploading(false);
        setGeneralError('');
        setRowErrors([]);
        setSuccessCount(null);
        setSkippedCount(0);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleClose = () => {
        if (uploading) return;
        resetState();
        onClose();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setGeneralError('');
        setRowErrors([]);
        setSuccessCount(null);
        setSkippedCount(0);
        setFileName(file.name);

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            setGeneralError(tr('bulk_performance_invalid_extension'));
            e.target.value = '';
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setGeneralError(tr('bulk_performance_file_too_large'));
            e.target.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            const res = await API.postFile('/reply/performance/bulk/', formData);
            setSuccessCount(res.data?.updated ?? 0);
            setSkippedCount(res.data?.skipped ?? 0);
            onImported();
        } catch (err) {
            const data = err.response?.data;
            if (Array.isArray(data?.errors) && data.errors.length) {
                setRowErrors(data.errors.map((e) => formatRowError(e, tr)));
            } else if (data?.code) {
                setGeneralError(formatGeneralError(data.code, data.params, tr));
            } else {
                setGeneralError(data?.message || tr('bulk_performance_generic_error'));
            }
        } finally {
            setUploading(false);
            // Se limpia el input para que volver a elegir el mismo archivo
            // (típico: corregir la planilla y guardarla en la misma ruta)
            // dispare de nuevo el change; si no, el navegador ve el mismo
            // value y no emite el evento.
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{tr('bulk_performance_upload_title')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: t.color.fgMuted, mb: 2 }}>
                    {tr('bulk_performance_upload_help')}
                </Typography>

                {successCount !== null && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {tr('bulk_performance_success').replace('%1', successCount)}
                        {skippedCount > 0 && ` ${tr('bulk_performance_skipped').replace('%1', skippedCount)}`}
                    </Alert>
                )}

                {generalError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{generalError}</Alert>
                )}

                {rowErrors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {tr('bulk_performance_row_errors')}
                    </Alert>
                )}
                {rowErrors.length > 0 && (
                    <Box sx={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${t.color.border}`, borderRadius: 1 }}>
                        <List dense disablePadding>
                            {rowErrors.map((msg) => (
                                <ListItem key={msg} divider>
                                    <ListItemText primaryTypographyProps={{ fontSize: 13 }} primary={msg} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {fileName && (
                        <Typography variant="body2" sx={{ color: t.color.fg }}>
                            {fileName}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon fontSize="small" />}
                        disabled={uploading}
                    >
                        {hasResult ? tr('bulk_performance_reupload') : tr('bulk_performance_choose_file')}
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".xlsx"
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                    {uploading && (
                        <Typography variant="body2" sx={{ color: t.color.fgMuted }}>
                            {tr('bulk_performance_uploading')}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    {tr('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
